import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSiteData } from '../../context/SiteDataContext';
import { claimAirdrop } from '../../services/api';
import { Send, ExternalLink, Gift } from 'lucide-react';

const Airdrop = () => {
  const { user } = useAuth();
  const { airdropConfig } = useSiteData();
  const tasks = airdropConfig?.tasks || [];
  const [form, setForm] = useState({ twitterUsername: '', retweetLink: '', telegramUsername: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const totalReward = tasks.reduce((sum, t) => sum + t.reward, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await claimAirdrop(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-center mb-4">
        <div className="flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 rounded-full px-5 py-2">
          <Gift size={16} className="text-purple-400" />
          <span className="text-purple-300 text-sm font-bold">{totalReward.toLocaleString()} MRS Reward</span>
        </div>
      </div>
      <h1 className="text-2xl lg:text-3xl font-bold text-white text-center mb-2">Airdrop Campaign</h1>
      <p className="text-gray-400 text-center mb-8">Complete the tasks below and claim your free tokens</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto mb-6">
        <a
          href={socialLinks.twitter || "https://x.com/marpicoinn?s=21"}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#0d1117] border border-[#1a2333] rounded-xl p-5 flex items-center gap-4 hover:border-yellow-400/30 transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-black border border-white/20 flex items-center justify-center group-hover:border-yellow-400/50">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24h-2.19L17.61 20.644Z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-white font-bold">X (Twitter)</p>
            <p className="text-gray-400 text-sm">Follow & Retweet</p>
          </div>
          <ExternalLink size={16} className="text-gray-500 group-hover:text-yellow-400" />
        </a>
        <a
          href={socialLinks.telegram || "https://t.me/marpicoin"}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#0d1117] border border-[#1a2333] rounded-xl p-5 flex items-center gap-4 hover:border-blue-400/30 transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-blue-400 flex items-center justify-center group-hover:shadow-[0_0_10px_rgba(96,165,250,0.3)]">
            <Send size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-bold">Telegram</p>
            <p className="text-gray-400 text-sm">Join Community</p>
          </div>
          <ExternalLink size={16} className="text-gray-500 group-hover:text-blue-400" />
        </a>
      </div>
      <div className="bg-[#0d1117] border border-[#1a2333] rounded-xl p-5 max-w-xl mx-auto mb-6">
        {tasks.map((task, i) => (
          <div key={task.id} className={`flex items-center gap-4 py-3 ${i < tasks.length - 1 ? 'border-b border-[#1a2333]' : ''}`}>
            <div className="w-7 h-7 rounded-full bg-[#1a2333] flex items-center justify-center text-gray-400 text-xs font-bold">{task.id}</div>
            <p className="flex-1 text-gray-300 text-sm">{task.task}</p>
            <span className="bg-yellow-400/10 text-yellow-400 text-xs font-bold px-2.5 py-1 rounded-md">+ {task.reward.toLocaleString()} MRS</span>
          </div>
        ))}
      </div>
      <div className="bg-[#0d1117] border border-[#1a2333] rounded-xl p-6 max-w-xl mx-auto">
        <h2 className="text-white font-bold text-lg mb-5">Submit Your Details</h2>
        {submitted && <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4 text-green-400 text-sm">Airdrop claim submitted! We will verify and credit your tokens.</div>}
        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
        {!submitted && (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-2 block">Twitter (X) Username</label>
              <div className="relative">
                <input type="text" value={form.twitterUsername} onChange={e => setForm({...form, twitterUsername: e.target.value})} placeholder="@username" className="w-full bg-[#1a2333] border border-[#2a3a4a] rounded-lg px-4 py-3 text-white text-sm focus:border-yellow-400/50 focus:outline-none pr-10" />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24h-2.19L17.61 20.644Z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-2 block">Retweet Link</label>
              <div className="relative">
                <input type="text" value={form.retweetLink} onChange={e => setForm({...form, retweetLink: e.target.value})} placeholder="https://x.com/..." className="w-full bg-[#1a2333] border border-[#2a3a4a] rounded-lg px-4 py-3 text-white text-sm focus:border-yellow-400/50 focus:outline-none pr-10" />
                <ExternalLink size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
            <div className="mb-6">
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-2 block">Telegram Username</label>
              <div className="relative">
                <input type="text" value={form.telegramUsername} onChange={e => setForm({...form, telegramUsername: e.target.value})} placeholder="@username" className="w-full bg-[#1a2333] border border-[#2a3a4a] rounded-lg px-4 py-3 text-white text-sm focus:border-yellow-400/50 focus:outline-none pr-10" />
                <Send size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="w-full py-3.5 rounded-xl font-bold text-black text-sm bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <Gift size={16} /> {submitting ? 'Submitting...' : `Claim ${totalReward.toLocaleString()} MRS`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Airdrop;
